import { useDispatch, useSelector } from 'react-redux';
import {
  useAttachCapacityComponentPointMutation,
  useGetCapacityComponentQuery,
  useGetTSPsQuery,
  useUpdateNoticeComponentCapacityMutation,
} from '../services/edgeApi';
import { toDateString, toIsoDate, toUTCDate } from '../utils/dateUtils';
import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
  MaintenanceNoticeTooltip,
  SOURCE_ABBREVIATIONS,
} from './MaintenanceNoticeTooltip';

import {
  TimelineCalendarRow,
  TimelineCalendarEvent,
} from '../utils/timelineUtils';
import {
  TimelineCalendar,
  TimelineCalendarRef,
} from '../helpers/TimelineCalendar';
import { selectTsps } from '../map/slice/mapSelectors';
import {
  colors,
  // generateColor,
  getContrastColor,
  setLuminosity,
} from '../utils/colors';
import {
  MaintenanceNoticeRecord,
  NoticeSourceType,
  NoticeDisplaySeverity,
} from '../types/api/NoticeRecord';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useDebouncedState, useTheme } from '../helpers/hooks';
import {
  FlagIcon,
  MapIcon,
  SearchIcon,
  WarningTriangleIcon,
} from '@iconicicons/react';
import { NoticeRecordModal } from '../notices/NoticeRecordModal';
import { noticeActions } from '../notices/noticeSlice';
import { useNoticeRecords } from '../hooks/useNoticeRecords';
import { titleize } from '../utils/stringUtils';
import { CapacityComponentSearch } from '../capacityComponents/CapacityComponentSearch';
import toast from 'react-hot-toast';
import { PointSelectorModal } from '../points/PointSelectorModal';
import { useSession } from '../hooks/useSession';

const EVENT_BORDER_WIDTH = 0;

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const SEVERITY_COLOR_MAP: Record<NoticeDisplaySeverity, string> = {
  [NoticeDisplaySeverity.OPEN]: colors.matteGreen,
  [NoticeDisplaySeverity.LOW]: colors.matteYellow,
  [NoticeDisplaySeverity.MEDIUM]: colors.matteRed,
  [NoticeDisplaySeverity.HIGH]: colors.mattePurple,
};

export enum MaintenanceCalendarGroupBy {
  PIPELINE = 'PIPELINE',
  LOCATION = 'LOCATION',
  ZONE = 'ZONE',
}

export enum MaintenanceCalendarView {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

const DEFAULT_CALLENDAR_SPAN = {
  [MaintenanceCalendarView.DAY]: 30,
  [MaintenanceCalendarView.WEEK]: 26,
  [MaintenanceCalendarView.MONTH]: 18,
};

const DEFAULT_ROW_WIDTH = {
  [MaintenanceCalendarGroupBy.PIPELINE]: 300,
  [MaintenanceCalendarGroupBy.LOCATION]: 300,
  [MaintenanceCalendarGroupBy.ZONE]: 250,
};

export type MaintenanceCalendarProps = {
  tspIds: number[];
  startDate: Date;
  columnUnit: MaintenanceCalendarView;
  groupBy: MaintenanceCalendarGroupBy;
  sourceTypes: NoticeSourceType[];
};

const MAP_BUILDER_PERMISSIONS = [
  'change_point',
  'change_edge',
  'change_interconnect',
  'change_node',
];

export const MaintenanceCalendar: React.FC<MaintenanceCalendarProps> = ({
  tspIds,
  startDate,
  columnUnit,
  groupBy,
  sourceTypes,
}) => {
  const calendarSpan = DEFAULT_CALLENDAR_SPAN[columnUnit];
  const _rowWidth = DEFAULT_ROW_WIDTH[groupBy];
  /**
   * This is the actual row width being used in the calendar.
   * It can be updated by the user resizing the row header.
   * The _rowWidth is the default value based on the groupBy option.
   */
  const [rowWidth, setRowWidth] = useState(_rowWidth);

  const [theme] = useTheme();
  const dispatch = useDispatch();

  const { permissions = [] } = useSession();

  const [searchText, setSearchText] = useDebouncedState('', 300);
  const calendarHeaderRef = useRef<HTMLDivElement>(null);
  const [selectedNotice, setSelectedNotice] =
    useState<MaintenanceNoticeRecord | null>(null);
  const [noticeToAttach, setNoticeToAttach] =
    useState<MaintenanceNoticeRecord | null>(null);
  const [addPointNotice, setAddPointNotice] =
    useState<MaintenanceNoticeRecord | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<TimelineCalendarRef>(null);

  const {
    data: addPointCapacityComponent,
    isFetching: addPointCapacityComponentLoading,
  } = useGetCapacityComponentQuery(
    {
      componentId: addPointNotice?.capacity_component_id ?? 0,
    },
    { skip: !addPointNotice?.capacity_component_id },
  );
  const [attachPointTrigger] = useAttachCapacityComponentPointMutation();
  const [updateNoticeComponentCapacity] =
    useUpdateNoticeComponentCapacityMutation();
  useGetTSPsQuery();

  const hasMapWritePermission = useMemo(() => {
    return MAP_BUILDER_PERMISSIONS.every((permission) =>
      permissions.includes(permission),
    );
  }, [permissions]);

  const calendarOptions = useMemo(() => {
    return {
      columnWidth: 300,
      columnWindowSize: calendarSpan,
      backgroundColor: 'var(--bs-body-bg)',
      eventHeight: 32,
      rowWidth: _rowWidth,
    };
  }, [calendarSpan, _rowWidth]);

  const startGasDate = useMemo(() => {
    const date = toIsoDate(startDate);
    if (columnUnit === MaintenanceCalendarView.WEEK) {
      const dayOfWeek = new Date(date).getUTCDay();
      const diffToMonday = (dayOfWeek + 6) % 7; // Calculate how many days to subtract to get to Monday
      const mondayDate = new Date(date);
      mondayDate.setUTCDate(mondayDate.getUTCDate() - diffToMonday);
      return toIsoDate(mondayDate);
    } else if (columnUnit === MaintenanceCalendarView.MONTH) {
      const monthDate = new Date(date);
      monthDate.setUTCDate(1); // Set to the first day of the month
      return toIsoDate(monthDate);
    }
    return date;
  }, [startDate, columnUnit, calendarOptions]);

  const endGasDate = useMemo(() => {
    const endDate = new Date(startDate.getTime());
    switch (columnUnit) {
      case MaintenanceCalendarView.DAY:
        endDate.setDate(startDate.getDate() + calendarSpan);
        break;
      case MaintenanceCalendarView.WEEK:
        endDate.setDate(startDate.getDate() + calendarSpan * 7);
        break;
      case MaintenanceCalendarView.MONTH:
        endDate.setMonth(startDate.getMonth() + calendarSpan);
        break;
      default:
        endDate.setDate(startDate.getDate() + calendarSpan);
        break;
    }
    return toIsoDate(endDate);
  }, [startDate, columnUnit, calendarSpan, calendarOptions]);

  const {
    data: notices,
    isFetching,
    next,
    refresh,
  } = useNoticeRecords({
    startGasDate,
    endGasDate,
    tspIds,
    sourceTypes,
    searchText,
  });

  const tsps = useSelector(selectTsps);

  const noticeTsps = useMemo(() => {
    const tspSet = new Set<string>();
    notices?.forEach((notice) => {
      if (notice.tsp) {
        tspSet.add(notice.tsp.toString());
      }
    });
    return Array.from(tspSet)
      .map((tspId) => tsps[tspId])
      .filter(Boolean);
  }, [notices, tsps]);

  const noticeLocations = useMemo(() => {
    const locationSet: Record<string, [string, string, number]> = {};
    notices?.forEach((notice) => {
      if (notice.notice_component) {
        locationSet[notice.notice_component.toString()] = [
          notice.notice_component.toString(),
          notice.identifier,
          notice.tsp,
        ];
      }
    });
    return Object.values(locationSet).map(([component, identifier]) => [
      component,
      identifier,
    ]);
  }, [notices]);

  const rows: TimelineCalendarRow[] = useMemo(() => {
    if (groupBy === MaintenanceCalendarGroupBy.PIPELINE) {
      return noticeTsps.map((tsp) => ({
        id: tsp.id.toString(),
        title: tsp.name,
      }));
    } else if (groupBy === MaintenanceCalendarGroupBy.LOCATION) {
      return noticeLocations.map(([location_id, location_name]) => ({
        id: location_id,
        title: location_name,
      }));
    }
    return [];
  }, [noticeTsps, noticeLocations, groupBy]);

  const events: TimelineCalendarEvent<{ notice: MaintenanceNoticeRecord }>[] =
    useMemo(() => {
      return (
        notices
          ?.filter(
            (notice) =>
              Boolean(notice.start_gas_date) && Boolean(notice.end_gas_date),
          )
          .map<TimelineCalendarEvent<{ notice: MaintenanceNoticeRecord }>>(
            (notice) => {
              const startDate = toUTCDate(notice.start_gas_date ?? new Date());
              const endDate = toUTCDate(notice.end_gas_date ?? new Date());
              if (columnUnit === MaintenanceCalendarView.WEEK) {
                const dayOfWeek = startDate.getUTCDay();
                const diffToMonday = (dayOfWeek + 6) % 7;
                startDate.setUTCDate(startDate.getUTCDate() - diffToMonday);
                const endDayOfWeek = endDate.getUTCDay();
                const diffToSunday = (7 - endDayOfWeek) % 7;
                endDate.setUTCDate(endDate.getUTCDate() - diffToSunday);
              } else if (columnUnit === MaintenanceCalendarView.MONTH) {
                startDate.setUTCDate(1);
                endDate.setUTCMonth(endDate.getUTCMonth() - 1);
                endDate.setUTCDate(13);
              }
              const rowId =
                groupBy === MaintenanceCalendarGroupBy.PIPELINE
                  ? notice.tsp?.toString() ?? 'unknown'
                  : groupBy === MaintenanceCalendarGroupBy.LOCATION
                  ? notice.notice_component.toString()
                  : 'unknown';
              return {
                id: notice.id.toString(),
                title: notice.identifier,
                row: rowId,
                segment: notice.identifier,
                start: startDate.getTime(),
                end: endDate.getTime(),
                properties: {
                  notice,
                },
              };
            },
          ) ?? []
      );
    }, [notices, groupBy, columnUnit]);

  const columnGenerator = useCallback(
    (offset: number) => {
      const date = new Date(startGasDate);
      let title = '';
      switch (columnUnit) {
        case MaintenanceCalendarView.DAY:
          date.setDate(date.getDate() + offset);
          title = toDateString(date);
          break;
        case MaintenanceCalendarView.WEEK:
          date.setDate(date.getDate() + offset * 7);
          const weekStart = new Date(date);
          const weekEnd = new Date(date);
          weekEnd.setDate(weekEnd.getDate() + 6);
          title = `${
            MONTH_NAMES[weekStart.getUTCMonth()]
          } ${weekStart.getUTCDate()} to ${
            MONTH_NAMES[weekEnd.getUTCMonth()]
          } ${weekEnd.getUTCDate()}`;
          break;
        case MaintenanceCalendarView.MONTH:
          // make sure that we are in the first day of the start + offset month
          date.setDate(13); // set to middle of the month to avoid DST issues
          date.setMonth(date.getMonth() + offset);
          date.setDate(0);
          title = `${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
          break;
      }

      return {
        id: date.toISOString(),
        title,
        numericValue: date.getTime(),
      };
    },
    [columnUnit, startGasDate],
  );

  const [headerHeight, setHeaderHeight] = useState(0);

  const handleResize = useCallback(() => {
    const newHeight = calendarHeaderRef.current?.offsetHeight ?? 0;
    setHeaderHeight(newHeight);
  }, [calendarHeaderRef]);

  useEffect(handleResize, [isFetching]);

  useEffect(() => {
    handleResize(); // Set initial height
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const renderRowHeader = useCallback(
    (
      row: TimelineCalendarRow,
      rowMeta: {
        rowEvents: TimelineCalendarEvent<{ notice: MaintenanceNoticeRecord }>[];
        segments: Array<{ title: string; colors: Set<number> }>;
      },
    ) => {
      const showSegments = groupBy === MaintenanceCalendarGroupBy.PIPELINE;
      return (
        <div className="d-flex h-100 w-100 justify-content-between">
          <div
            style={{
              position: 'relative',
              width: showSegments ? '40%' : '100%',
            }}
          >
            <p
              className="px-3 text-center"
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                // transform: 'rotate(-90deg)',
                position: 'sticky',
                top: 60,
              }}
              title={row.title}
            >
              {row.title}
            </p>
          </div>
          {showSegments && (
            <div
              style={{
                width: '60%',
                height: '100%',
              }}
            >
              {rowMeta.segments.map((segment, index) => {
                const colorCount = segment.colors.size;
                const segmentEvents = rowMeta.rowEvents.filter(
                  (event) => event.segment === segment.title,
                );
                return (
                  <div
                    key={index}
                    className="d-flex justify-content-end align-items-center pe-2"
                    style={{
                      height: `${colorCount * calendarOptions.eventHeight}px`,
                      borderBottom: '1px solid var(--bs-border-color)',
                    }}
                    title={segment.title}
                  >
                    <span
                      // className="h-100 d-flex align-items-center"
                      style={{
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        width: '100%',
                      }}
                    >
                      {segmentEvents.some(
                        (event) =>
                          !event.properties?.notice?.has_capacity_component,
                      ) ? (
                        <span
                          title="No capacity component"
                          role={hasMapWritePermission ? 'button' : undefined}
                          onClick={
                            hasMapWritePermission
                              ? () =>
                                  setNoticeToAttach(
                                    segmentEvents[0]?.properties?.notice ??
                                      null,
                                  )
                              : undefined
                          }
                        >
                          <WarningTriangleIcon
                            className="me-2"
                            style={{
                              width: '1rem',
                              height: '1rem',
                              color: 'var(--bs-danger)',
                            }}
                          />
                        </span>
                      ) : segmentEvents.some(
                          (event) => !event.properties?.notice?.has_point,
                        ) ? (
                        <span
                          title="Notice is not assigned to a point"
                          role={hasMapWritePermission ? 'button' : undefined}
                          onClick={
                            hasMapWritePermission
                              ? (e) => {
                                  e.stopPropagation();
                                  setAddPointNotice(
                                    segmentEvents[0]?.properties?.notice ??
                                      null,
                                  );
                                }
                              : undefined
                          }
                        >
                          <FlagIcon
                            className="me-2"
                            style={{
                              width: '1rem',
                              height: '1rem',
                              color: 'var(--bs-danger)',
                            }}
                          />
                        </span>
                      ) : (
                        segmentEvents.some(
                          (event) => !event.properties?.notice?.has_node,
                        ) && (
                          <span title="Notice is not mapped">
                            <MapIcon
                              className="me-2"
                              style={{
                                width: '1rem',
                                height: '1rem',
                                color: 'var(--bs-danger)',
                              }}
                            />
                          </span>
                        )
                      )}
                      {segment.title}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    },
    [hasMapWritePermission],
  );

  const onResize = useCallback((dimensions: { rowWidth?: number }) => {
    if (dimensions.rowWidth !== undefined) {
      setRowWidth(dimensions.rowWidth);
    }
  }, []);

  const renderEvent = useCallback(
    (
      maintenance: TimelineCalendarEvent<{
        notice: MaintenanceNoticeRecord;
      }>,
      style: React.CSSProperties,
    ) => {
      const notice = maintenance.properties?.notice;
      // const colorKey = notice.tsp;
      if (!notice) return null;
      const severity =
        notice.display_values.severity ?? NoticeDisplaySeverity.LOW;
      const color = SEVERITY_COLOR_MAP[severity] ?? colors.matteRed;
      // const barColor =
      //   theme === 'light'
      //     ? generateColor(colorKey || 0)
      //     : generateColor(colorKey || 0, 50, 40);
      const borderColor =
        theme === 'light' ? setLuminosity(color, 30) : setLuminosity(color, 85);
      const textColor = getContrastColor(color);
      const sourceTitle = titleize(
        notice.source_type?.split('_').join(' ') ?? '',
      );
      return (
        <>
          <div
            role="button"
            style={{
              backgroundColor: color,
              border: `1px solid ${borderColor}`,
              // borderLeft: 0,
              borderRadius: '4px',
              boxSizing: 'border-box',
              padding: '0px',
              color: textColor,
              fontSize: '12px',
              overflow: 'clip',
              overflowClipMargin: 'border-box',
              position: 'relative',
              ...style,
            }}
            className="d-flex align-items-center fw-semibold justify-content-between"
            id={`maintenance-${maintenance.id}`}
            onClick={() => {
              setSelectedNotice(notice);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              dispatch(
                noticeActions.setNoticeContextMenu({
                  notice,
                  position: { x: e.clientX, y: e.clientY },
                }),
              );
            }}
          >
            <div
              style={{
                position: 'sticky',
                overflow: 'visible',
                left: rowWidth,
                backgroundColor: color,
                paddingRight: 4,
                zIndex: 1,
                marginLeft: 2,
              }}
            >
              <div
                className="d-inline p-0 m-0"
                style={{
                  content: ' ',
                  width: EVENT_BORDER_WIDTH,
                  height: 'calc(100% + 2px)',
                  position: 'sticky',
                  left: rowWidth,
                  zIndex: 2,
                  overflow: 'visible',
                  backgroundColor: borderColor,
                }}
              ></div>
              <span
                style={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  position: 'sticky',
                  left: rowWidth + EVENT_BORDER_WIDTH, // row width + border
                  paddingLeft: 4,
                  paddingTop: 2,
                  paddingBottom: 2,
                  backgroundColor: 'transparent',
                }}
                className="d-flex align-items-center"
              >
                {notice.display_values.text}
              </span>
            </div>
            <span
              className="px-2 me-1"
              style={{
                position: 'sticky',
                right: 4,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                backgroundColor: 'var(--bs-secondary-bg)',
                color: 'var(--bs-body-color)',
                borderRadius: '16px',
                fontSize: '0.7rem',
              }}
              title={sourceTitle}
            >
              {notice.source_type
                ? SOURCE_ABBREVIATIONS[notice.source_type] ?? sourceTitle
                : null}
            </span>
          </div>
          <UncontrolledTooltip
            target={`maintenance-${maintenance.id}`}
            placement="bottom-start"
            innerClassName="mw-100"
            innerstyle={{
              width: 'max-content',
              maxWidth: '500px',
            }}
          >
            <MaintenanceNoticeTooltip notice={notice} />
          </UncontrolledTooltip>{' '}
        </>
      );
    },
    [theme, rowWidth, hasMapWritePermission],
  );

  return (
    <>
      <div
        ref={calendarHeaderRef}
        className="p-2 ps-4"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backgroundColor: 'var(--bs-body-bg)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="input-group mb-2" style={{ maxWidth: 400 }}>
            <span className="input-group-text">
              <SearchIcon />{' '}
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by location or pipeline..."
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
            />
          </div>
          <div
            className="d-flex gap-2 justify-content-end align-items-center"
            style={{ minWidth: 200 }}
          >
            Severity:{' '}
            {[
              NoticeDisplaySeverity.OPEN,
              NoticeDisplaySeverity.LOW,
              NoticeDisplaySeverity.MEDIUM,
              NoticeDisplaySeverity.HIGH,
            ].map((severity) => {
              const color = SEVERITY_COLOR_MAP[severity] ?? colors.matteRed;
              const borderColor =
                theme === 'light'
                  ? setLuminosity(color, 30)
                  : setLuminosity(color, 85);
              return (
                <div
                  key={severity}
                  className="d-flex gap-1 align-items-center"
                  style={{
                    fontSize: '0.9rem',
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: color,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 4,
                      display: 'inline-block',
                      content: ' ',
                    }}
                  ></span>{' '}
                  <span className="align-middle">{severity}</span>
                </div>
              );
            })}
          </div>
        </div>
        {isFetching && events.length === 0 && (
          <div className="d-flex w-100 h-100 justify-content-center align-items-center my-4">
            <LoadingIndicator />
          </div>
        )}
      </div>
      {events.length > 0 && (
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: `calc(100% - ${headerHeight}px)`,
            overflowX: 'auto',
            position: 'relative',
          }}
        >
          <div style={{ maxWidth: '100%' }}>
            <TimelineCalendar
              ref={calendarRef}
              rows={rows}
              columnGenerator={columnGenerator}
              events={events}
              renderRowHeader={renderRowHeader}
              renderEvent={renderEvent}
              onScrollY={next}
              onResize={onResize}
              options={calendarOptions}
            />
          </div>
          {isFetching && (
            <div
              className="d-flex justify-content-center align-items-center my-4"
              style={{ position: 'sticky', left: 0 }}
            >
              <LoadingIndicator />
            </div>
          )}
        </div>
      )}
      {isFetching === false && events.length === 0 && (
        <div className="d-flex w-100 justify-content-center align-items-center my-4">
          <p className="text-muted">No notices found.</p>
        </div>
      )}
      <NoticeRecordModal
        noticeRecordId={selectedNotice?.id}
        isOpen={selectedNotice !== null}
        onClose={() => setSelectedNotice(null)}
      />
      {hasMapWritePermission && (
        <>
          <CapacityComponentSearch
            isSearching={noticeToAttach !== null}
            onClose={() => setNoticeToAttach(null)}
            onSelect={async (component) => {
              let toastId = '';
              setNoticeToAttach(null);
              if (noticeToAttach?.notice_component) {
                try {
                  toastId = toast.loading(
                    'Assigning capacity component, please wait...',
                  );
                  await updateNoticeComponentCapacity({
                    componentId: noticeToAttach?.notice_component,
                    capacityId: component.id,
                  });
                  toast.loading('Updating records, please wait...', {
                    id: toastId,
                  });
                  await refresh();
                  toast.success('Capacity component assigned successfully', {
                    id: toastId,
                  });
                } catch (error) {
                  toast.error('Failed to assign capacity component', {
                    id: toastId,
                  });
                }
              }
            }}
            filters={{
              textSearch: noticeToAttach?.identifier.split('(')[0].trim() ?? '',
              tsps: noticeToAttach?.tsp ? [noticeToAttach.tsp] : [],
            }}
          />
          <PointSelectorModal
            isOpen={addPointNotice !== null}
            isLoading={addPointCapacityComponentLoading}
            onClose={() => setAddPointNotice(null)}
            onSelect={async (point) => {
              let toastId = '';
              setAddPointNotice(null);
              if (addPointCapacityComponent) {
                try {
                  toastId = toast.loading('Assigning point, please wait...');
                  await attachPointTrigger({
                    component: addPointCapacityComponent,
                    point,
                  });
                  toast.loading('Updating records, please wait...', {
                    id: toastId,
                  });
                  await refresh();
                  toast.success('Point assigned successfully', {
                    id: toastId,
                  });
                } catch (error) {
                  toast.error('Failed to assign point', { id: toastId });
                }
              }
            }}
            lockedTsps={addPointNotice?.tsp ? [addPointNotice.tsp] : []}
            defaultFilters={{
              searchText: addPointNotice?.identifier.split('(')[0].trim() ?? '',
            }}
          />
        </>
      )}
    </>
  );
};
